import React, { useEffect, useRef, useState } from 'react';
import { Popover, Select, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  CALC_METHODOLOGIES,
  CALC_METHODOLOGY_DESCRIPTIONS,
  type CalcMethodology,
} from '../../../services/calcMethodology/calcMethodologyService';
import {
  useCalcMethodology,
  useCalcMethodologyLog,
  useCanChangeCalcMethodology,
  useCanViewCalcMethodology,
  useRefetchOnCalcMethodologyChange,
  useSetCalcMethodology,
} from '../../../hooks/useCalcMethodology';
import { usePlanningYear } from '../../../hooks/usePlanningYear';
import { confirmDialog } from '../../../utils/confirmDialogs';
import { appNotify } from '../../../utils/notifications';


const formatTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '';

const MethodologyLog: React.FC<{ year: number }> = ({ year }) => {
  const { data, isLoading, isError, canRead } = useCalcMethodologyLog(year, true);
  if (!canRead) return null;
  return (
    <div>
      <strong>Последние переключения ({year} год)</strong>
      {isLoading ? <div>Загрузка…</div>
        : isError ? <div>Не удалось загрузить журнал</div>
        : !data?.length ? <div>Переключений не было</div>
        : (
          <ul className="calc-methodology-log">
            {data.map((entry) => (
              <li key={entry.id}>
                {formatTime(entry.changedAt)}: {entry.previousMethodology ?? '—'} → {entry.newMethodology},{' '}
                {entry.changedByName || entry.changedByEmail || 'неизвестно'}
              </li>
            ))}
          </ul>
        )}
    </div>
  );
};

const MethodologyInfo: React.FC<{ year: number; showLog: boolean }> = ({ year, showLog }) => (
  <div className="calc-methodology-info">
    <p>
      Методология определяет, какие рассчитанные в БД значения показывают этапы 2–4 и сверка
      и что попадает в утверждённые версии. Приложение по этому описанию ничего не пересчитывает.
    </p>
    <p>
      Выбирается для каждого года планирования отдельно (сейчас — {year}). Для 2026 года действует
      только методология 2026; с 2027 года по умолчанию — 2027.
    </p>
    {CALC_METHODOLOGIES.map((methodology) => (
      <div key={methodology}>
        <strong>{CALC_METHODOLOGY_DESCRIPTIONS[methodology].title}</strong>
        <div>Цена продажи по компании — первая доступная из:</div>
        <ol>
          {CALC_METHODOLOGY_DESCRIPTIONS[methodology].priceOrder.map((step) => <li key={step}>{step}</li>)}
        </ol>
      </div>
    ))}
    <p>
      Цена по компании: выручка с 1 января предыдущего года / кол-во упаковок, с актуализацией до
      31 декабря года планирования. Планы продаж в рублях — кол-во × цена выбранной методологии.
    </p>
    <p>
      Смена применяется сразу для всех пользователей. Утверждённые версии хранят значения той
      методологии, с которой были утверждены, и при переключении не меняются.
    </p>
    {showLog && <MethodologyLog year={year} />}
  </div>
);

/**
 * ⓘ с описанием методологий — для всех ролей и всех лет: право на методологию решает,
 * видно ли переключатель, но прочитать, как устроена математика, может каждый.
 * Журнал переключений внутри — только с правом «Чтение» (см. MethodologyLog).
 */
const MethodologyInfoIcon: React.FC<{ year: number }> = ({ year }) => {
  // Журнал запрашиваем только когда подсказку открыли.
  const [infoOpen, setInfoOpen] = useState(false);
  return (
    <Popover
      title="Методология расчёта этапов 2–4"
      content={<MethodologyInfo year={year} showLog={infoOpen} />}
      onOpenChange={setInfoOpen}
      trigger={['hover', 'click']}
      placement="bottomRight"
    >
      <InfoCircleOutlined className="header-calc-methodology-info" aria-label="О методологиях" tabIndex={0} />
    </Popover>
  );
};

/** Выбор методологии — для ролей, которым методология видна. */
const CalcMethodologyControl: React.FC<{ year: number }> = ({ year }) => {
  const { methodology, state } = useCalcMethodology(year);
  const canChange = useCanChangeCalcMethodology();
  const setMethodology = useSetCalcMethodology();

  // Сообщаем о смене, сделанной другим пользователем (своя — в handleChange).
  // Смена года планирования переключением не считается.
  const ownChangeRef = useRef(false);
  const shownRef = useRef<{ year: number; methodology: CalcMethodology } | undefined>(undefined);
  useEffect(() => {
    if (methodology === undefined) return;
    const previous = shownRef.current;
    shownRef.current = { year, methodology };
    if (!previous || previous.year !== year || previous.methodology === methodology) return;
    if (ownChangeRef.current) {
      ownChangeRef.current = false;
      return;
    }
    appNotify.info(
      `Методология ${year} года изменена: ${previous.methodology} → ${methodology}`,
      `${state?.updatedBy ? `Переключил(а) ${state.updatedBy}` : 'Переключено'}${state?.updatedAt ? ` ${formatTime(state.updatedAt)}` : ''}. ` +
        'Данные этапов 2–4 перезагружены по новой методологии; утверждённые версии не изменились.',
    );
  }, [year, methodology, state?.updatedAt, state?.updatedBy]);

  const handleChange = async (next: CalcMethodology) => {
    if (methodology === undefined || next === methodology) return;
    const previous = methodology;
    const confirmed = await confirmDialog.custom({
      title: `Переключить методологию ${year} года: ${previous} → ${next}?`,
      content:
        `Изменение применяется сразу для всех пользователей: этапы 2–4 и сверка за ${year} год будут показывать ` +
        `значения методологии ${next}, новые утверждённые версии сохранят их. Уже утверждённые версии ` +
        'не изменятся. Несохранённые изменения на открытых этапах лучше сохранить до переключения.',
      okText: 'Переключить',
    });
    if (!confirmed) return;

    ownChangeRef.current = true;
    setMethodology.mutate({ year, methodology: next }, {
      onSuccess: (change) => {
        if (!change.changed) ownChangeRef.current = false;
        appNotify.info(
          `Методология ${change.year} года изменена: ${change.previous} → ${change.current}`,
          `Применено ${formatTime(change.changedAt)} для всех пользователей. Этапы 2–4 и сверка ` +
            `перезагружаются по методологии ${change.current}; утверждённые версии не изменились.`,
          { persistToHistory: true },
        );
      },
      onError: (error) => {
        ownChangeRef.current = false;
        appNotify.error('Не удалось переключить методологию', error instanceof Error ? error.message : undefined);
      },
    });
  };

  // Для года без выбора (2026) переключателя нет.
  if (!state || state.available.length < 2) return null;

  const select = (
    <Select<CalcMethodology>
      value={methodology}
      onChange={handleChange}
      options={state.available.map((value) => ({ value, label: `Мет. ${value}` }))}
      size="small"
      loading={methodology === undefined || setMethodology.isPending}
      disabled={!canChange || setMethodology.isPending}
      placeholder="Мет."
      aria-label={`Методология расчёта этапов 2–4, ${year} год`}
      className="header-calc-methodology"
    />
  );

  return canChange ? select : (
    <Tooltip title="Только просмотр: выбирать методологию могут роли, которым администратор выдал это право">
      <span>{select}</span>
    </Tooltip>
  );
};

/**
 * Методология этапов 2–4 для выбранного года планирования (в шапке). Доступ —
 * ресурс sales_calc_methodology в редакторе прав ролей: «Нет доступа» — только ⓘ
 * с описанием математики, «Чтение» — переключатель без выбора и журнал в ⓘ,
 * «Запись»/«Полный доступ» — переключают (сервер проверяет то же право).
 * Для года без выбора (2026) переключателя нет ни у кого, ⓘ — у всех.
 */
export const CalcMethodologySwitch: React.FC = () => {
  const [year] = usePlanningYear();
  const { methodology } = useCalcMethodology(year);
  const canView = useCanViewCalcMethodology();
  // Данные этапов перечитываются после смены и у тех, кому методология не видна.
  useRefetchOnCalcMethodologyChange(year, methodology);

  return (
    <div className="header-calc-methodology-group">
      {canView && <CalcMethodologyControl year={year} />}
      <MethodologyInfoIcon year={year} />
    </div>
  );
};
